import React, { useMemo, useRef, useState } from 'react';
import { Download, FileSearch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { IMPORT_CONTRACTS, parseProductionCsv } from '@/adapters/importContract';
import { fmtNumber } from '@/domain/numbers';
import { formatDateShort } from '@/domain/time';
import { Panel, PanelHeader } from './States';
import { CoverageBadge, Note } from './Badges';
import { DefList } from './PageHeader';
import { cn } from '@/lib/utils';

const TYPE_LABELS = { string: 'tekst', number: 'tall', datetime_utc: 'tidspunkt (UTC, Z)', enum: 'liste', integer: 'heltall' };

function FieldTable({ contract }) {
  return (
    <div className="hr-scroll overflow-x-auto">
      <Table data-testid={`contract-fields-${contract.id}`} className="[&_td]:py-2 [&_th]:h-9 text-[14px]">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="pl-5">Kolonne</TableHead>
            <TableHead>Påkrevd</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Enhet</TableHead>
            <TableHead>Normalisert felt</TableHead>
            <TableHead className="pr-5">Beskrivelse</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contract.fields.map((f) => (
            <TableRow key={f.column} className="hover:bg-transparent">
              <TableCell className="pl-5 font-mono text-[13px]">{f.column}</TableCell>
              <TableCell>{f.required ? <span className="font-medium text-[var(--hr-heading)]">ja</span> : <span className="text-[var(--hr-muted)]">valgfri</span>}</TableCell>
              <TableCell className="text-[var(--hr-muted)]">
                {TYPE_LABELS[f.type]}
                {f.allowed && <div className="font-mono text-[12px]">{f.allowed.join(' | ')}</div>}
              </TableCell>
              <TableCell className="hr-tabular">{f.unit ?? '–'}</TableCell>
              <TableCell className="font-mono text-[13px]">{f.normalizedField}</TableCell>
              <TableCell className="max-w-[360px] pr-5 text-[var(--hr-muted)]">{f.description}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ContractRules({ contract }) {
  return (
    <div className="px-5 py-4">
      <DefList
        cols={2}
        items={[
          { label: 'Kildesystem', value: contract.systemLabel },
          { label: 'Identitet', value: contract.identity },
          { label: 'Tid og tidssone', value: contract.timeRule },
          { label: 'Duplikater', value: contract.duplicateRule },
          { label: 'Dekning og manglende verdier', value: contract.coverageRule },
          {
            label: 'Eksempelfil (samme testvindu 2. oktober 2026)',
            value: (
              <Button asChild variant="outline" size="sm" className="h-8">
                <a href={contract.exampleFile} download data-testid={`contract-example-${contract.id}`}>
                  <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Last ned eksempel
                </a>
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
}

/** Tørrkjøring av PLS-kontrakten: validering, mapping-forhåndsvisning og oppsummering. Endrer aldri datasettet. */
function PlsDryRun({ lineIds, fractionIds }) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    setParsed(null);
    if (!f) return;
    setFile(f);
    const text = await f.text();
    setParsed(parseProductionCsv(text, lineIds, fractionIds));
  };
  const errorsByRow = useMemo(() => {
    const m = new Map();
    for (const e of parsed?.errors ?? []) m.set(e.row, [...(m.get(e.row) ?? []), e]);
    return m;
  }, [parsed]);
  const preview = parsed?.rows.slice(0, 12) ?? [];

  return (
    <div className="space-y-4 border-t border-[var(--hr-hairline)] px-5 py-4" data-testid="pls-dry-run">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Label htmlFor="pls-file" className="text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">Kontraktskontroll (tørrkjøring) – velg PLS-fil</Label>
          <Input ref={inputRef} id="pls-file" type="file" accept=".csv,text/csv" onChange={onFile} className="mt-1 bg-white" data-testid="pls-dry-run-input" />
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" className="h-9">
            <a href="/maler/pls-eksempel-ugyldig.csv" download data-testid="contract-example-pls-invalid"><Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Ugyldig eksempel</a>
          </Button>
        </div>
      </div>
      <p className="text-[13px] leading-5 text-[var(--hr-muted)]">Validerer filen mot kontrakten og viser hvordan kolonnene mappes til normaliserte felt. Datasettet og beregningene endres ikke – PLS-import tas i bruk først når eksportformat og master for hvert felt er bekreftet med kunden.</p>
      {parsed && (
        <div className="space-y-3" data-testid="pls-dry-run-result">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{file?.name}</span>
            <span className="text-[var(--hr-muted)]">· {parsed.rowCount} rader · skilletegn «{parsed.delimiter}»</span>
            {parsed.ok ? <CoverageBadge status="complete" label="Validert uten feil" /> : <CoverageBadge status="missing" label={`${parsed.headerErrors.length + parsed.errors.length} feil – filen ville blitt avvist`} />}
          </div>
          <div>
            <div className="mb-1.5 text-[13px] font-medium uppercase tracking-wide text-[var(--hr-muted)]">Mapping-forhåndsvisning</div>
            <ul className="flex flex-wrap gap-1.5" data-testid="pls-mapping">
              {parsed.mapping.map((m) => (
                <li key={m.column} className={cn('rounded-md border px-2 py-0.5 font-mono text-[12px]', m.status === 'ok' ? 'border-[var(--hr-border)] bg-white' : m.status === 'mangler' ? 'border-[rgba(180,35,24,0.4)] bg-[var(--hr-red-bg)] text-[var(--hr-red)]' : 'border-[rgba(154,91,0,0.3)] bg-[var(--hr-amber-bg)] text-[var(--hr-amber)]')}>
                  {m.column} → {m.normalizedField}
                  {m.status !== 'ok' && <span className="ml-1 font-sans">({m.status})</span>}
                </li>
              ))}
            </ul>
          </div>
          {parsed.headerErrors.length > 0 && (
            <Note tone="red" data-testid="pls-header-errors">
              <ul className="list-disc pl-4">
                {parsed.headerErrors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </Note>
          )}
          {parsed.errors.length > 0 && (
            <div className="overflow-x-auto rounded-[var(--hr-radius-sm)] border border-[rgba(180,35,24,0.3)]">
              <Table data-testid="pls-row-errors">
                <TableHeader>
                  <TableRow>
                    <TableHead>Rad</TableHead>
                    <TableHead>Kolonne</TableHead>
                    <TableHead>Verdi</TableHead>
                    <TableHead>Grunn</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsed.errors.slice(0, 30).map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="hr-tabular">{e.row}</TableCell>
                      <TableCell className="font-mono text-[13px]">{e.column}</TableCell>
                      <TableCell className="font-mono text-[13px]">{e.value}</TableCell>
                      <TableCell>{e.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {parsed.warnings.length > 0 && (
            <Note tone="amber">
              {parsed.warnings.slice(0, 5).map((w) => (
                <div key={w}>{w}</div>
              ))}
            </Note>
          )}
          {parsed.ok && (
            <div className="hr-inset px-4 py-3 text-sm" data-testid="pls-dry-run-summary">
              <div className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--hr-muted-2)]">Normalisert resultat (ikke tatt i bruk)</div>
              <div className="hr-tabular mt-1 leading-6">
                {parsed.summary.production} produksjonsposter · {parsed.summary.lineState} linjetilstander · {fmtNumber(parsed.summary.tonnes, 1)} t produsert · {fmtNumber(parsed.summary.productionHours, 2)} h produksjon · {fmtNumber(parsed.summary.stopHours, 2)} h stopp · døgn (UTC-dato): {parsed.summary.days.map(formatDateShort).join(', ') || '–'}
              </div>
            </div>
          )}
          {preview.length > 0 && (
            <div className="max-h-[260px] overflow-auto rounded-[var(--hr-radius-sm)] border border-[var(--hr-border)]">
              <Table data-testid="pls-preview-table" className="text-[13px]">
                <TableHeader className="sticky top-0 bg-white">
                  <TableRow>
                    <TableHead>Rad</TableHead>
                    <TableHead>record_id</TableHead>
                    <TableHead>type</TableHead>
                    <TableHead>from_utc</TableHead>
                    <TableHead>to_utc</TableHead>
                    <TableHead>fraksjon</TableHead>
                    <TableHead className="text-right">tonn</TableHead>
                    <TableHead>state</TableHead>
                    <TableHead>Feil</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.map((row) => {
                    const errs = errorsByRow.get(row.row) ?? [];
                    return (
                      <TableRow key={row.row} className={cn(errs.length && 'bg-[var(--hr-red-bg)]')}>
                        <TableCell className="hr-tabular">{row.row}</TableCell>
                        <TableCell className="font-mono">{row.recordId}</TableCell>
                        <TableCell>{row.recordType}</TableCell>
                        <TableCell className="font-mono">{row.fromUtc}</TableCell>
                        <TableCell className="font-mono">{row.toUtc}</TableCell>
                        <TableCell>{row.fractionId ?? '–'}</TableCell>
                        <TableCell className="hr-tabular text-right">{row.producedTonnes ?? (row.recordType === 'production' ? 'null' : '')}</TableCell>
                        <TableCell>{row.state ?? ''}</TableCell>
                        <TableCell className="text-[var(--hr-red)]">{errs.map((e) => e.column).join(', ')}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Importkontrakt for fremtidige kilder – forberedelse, ingen reell integrasjon. */
export function ImportContractPanel({ lineIds, fractionIds }) {
  return (
    <Panel data-testid="import-contract-panel">
      <PanelHeader
        title="Importkontrakt for PLS og VisionLink (forberedelse)"
        description="Normalisert kontrakt per kilde: felt, enheter, tidsstempel/tidssone, maskin- og linjeidentitet, kildeangivelse, dekning, eksempelfiler og duplikatregel. Begge eksempelfilene dekker samme testvindu (2. oktober 2026) slik at kildene kan kontrolleres mot hverandre. Ingen leverandørendepunkt er koblet, og ingen tilgang etterspørres før datakilde og eksportformat er avklart."
        actions={<FileSearch className="h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" />}
      />
      <Tabs defaultValue="pls" className="w-full">
        <TabsList className="mx-4 mt-4 flex h-auto w-auto flex-wrap justify-start gap-1 p-1 sm:mx-5 sm:inline-flex">
          {IMPORT_CONTRACTS.map((c) => (
            <TabsTrigger key={c.id} value={c.id} className="h-8 text-[14px]" data-testid={`contract-tab-${c.id}`}>
              {c.title}
            </TabsTrigger>
          ))}
        </TabsList>
        {IMPORT_CONTRACTS.map((c) => (
          <TabsContent key={c.id} value={c.id} className="mt-2">
            <ContractRules contract={c} />
            <FieldTable contract={c} />
            {c.id === 'pls' && <PlsDryRun lineIds={lineIds} fractionIds={fractionIds} />}
            {c.id === 'visionlink' && (
              <p className="border-t border-[var(--hr-hairline)] px-5 py-4 text-[13px] leading-5 text-[var(--hr-muted)]">
                VisionLink-kontrakten valideres og tas i bruk gjennom «Kontrollert CSV-import» over. Kolonnen <span className="font-mono">reported_co2_kg</span> gir rapportert CO₂ som prioriteres foran estimat fra diesel for det intervallet; øvrige intervaller estimeres fortsatt. Rapportert og estimert summeres aldri for samme intervall.
              </p>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </Panel>
  );
}
